/**
 * LegalTemplates PDF Editor — queued OCR after a redacted save
 *
 * A redacted save flattens the affected pages to images. The server restores
 * their searchable text with Tesseract: inline for a few pages, in a queue
 * job for more. This module polls the job's status and, when the text layer
 * lands, swaps the stored bytes in underneath the person's work without
 * moving them off the page they are on.
 */
import { pagesEl, session, state } from './context.js';
import { fetchPdfBytes, withCacheBust } from './io.js';
import { setStatus, showAlert } from './ui.js';
import { renderAllPages, setActiveThumb } from './viewport.js';

const POLL_INTERVAL_MS = 4000;
const POLL_LIMIT = 150; // ten minutes

export function stopOcrPolling() {
    if (session.ocrPoll && session.ocrPoll.timer) {
        window.clearTimeout(session.ocrPoll.timer);
    }
    session.ocrPoll = null;
    document.documentElement.removeAttribute('data-pdf-ocr');
}

export function startOcrPolling(statusUrl) {
    stopOcrPolling();
    if (!statusUrl) {
        return;
    }
    const poll = { url: String(statusUrl), timer: null, attempts: 0 };
    session.ocrPoll = poll;
    document.documentElement.setAttribute('data-pdf-ocr', 'queued');
    poll.timer = window.setTimeout(() => pollOcrStatus(poll), POLL_INTERVAL_MS);
}

async function pollOcrStatus(poll) {
    if (session.ocrPoll !== poll) {
        return;
    }
    poll.attempts += 1;

    let payload = null;
    try {
        const response = await fetch(withCacheBust(poll.url), {
            headers: { 'Accept': 'application/json' },
            credentials: 'same-origin',
        });
        payload = response.ok ? await response.json().catch(() => null) : null;
    } catch (error) {
        payload = null;
    }
    if (session.ocrPoll !== poll) {
        return;
    }

    const status = String((payload && payload.status) || 'none');
    document.documentElement.setAttribute('data-pdf-ocr', status);

    if (status === 'queued' || status === 'running' || (payload === null && poll.attempts < POLL_LIMIT)) {
        if (poll.attempts >= POLL_LIMIT) {
            stopOcrPolling();
            return;
        }
        poll.timer = window.setTimeout(() => pollOcrStatus(poll), POLL_INTERVAL_MS);
        return;
    }

    // Terminal: done | stale | unavailable | failed | none.
    session.ocrPoll = null;
    if (status === 'done') {
        await refreshDocumentAfterOcr(poll, payload);
        return;
    }
    if (payload && payload.message) {
        showAlert(payload.message, false);
    }
}

/**
 * Pull the stored bytes (now carrying the text layer) back in. Annotations,
 * form values and the operation log stay: the page count and geometry are
 * unchanged, only the invisible text changed.
 */
async function refreshDocumentAfterOcr(poll, payload) {
    if (state.interaction || state.historyBusy) {
        // Mid-drag or mid-undo: try again shortly.
        session.ocrPoll = poll;
        poll.timer = window.setTimeout(() => refreshDocumentAfterOcr(poll, payload), 1500);
        return;
    }
    session.ocrPoll = null;

    const source = withCacheBust(session.sourceUrl);
    if (!source) {
        return;
    }

    const scroller = findScrollParent(pagesEl);
    const scrollTop = scroller ? scroller.scrollTop : window.scrollY;
    const activePage = state.lastActivePage;

    try {
        setStatus('Restoring searchable text');
        const generation = state.renderGeneration;
        const bytes = await fetchPdfBytes(source);
        if (state.renderGeneration !== generation || session.ocrPoll) {
            return;
        }
        state.basePdfBytes = bytes;
        await renderAllPages();
        if (scroller) {
            scroller.scrollTop = scrollTop;
        } else {
            window.scrollTo(0, scrollTop);
        }
        if (activePage) {
            state.lastActivePage = activePage;
            setActiveThumb(activePage);
        }
        setStatus('Searchable text restored');
        showAlert((payload && payload.message) || 'Searchable text was restored on the redacted pages.');
        document.documentElement.setAttribute('data-pdf-ocr', 'refreshed');
    } catch (error) {
        setStatus('Editor ready');
    }
}

function findScrollParent(element) {
    let node = element ? element.parentElement : null;
    while (node && node !== document.body) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
        }
        node = node.parentElement;
    }

    return null;
}
